import { useCallback, useSyncExternalStore } from 'react';
import {
  cookieKeys,
  getCookieStore,
  MARKETING_THEME_COOKIE_MAX_AGE_SECONDS,
} from 'core/storage/cookies';
import type { ThemeMode } from 'hooks/useTheme';

// Marketing (visitor) pages' colour theme. Deliberately separate from the
// app's `useTheme` (dashboard): its own storage and its own class —
// `marketing-dark` on <html>, which only the marketing `marketing-dark:`
// utilities key off (see the @custom-variant in styles/globals.css). The
// class is also set pre-paint by app/[locale]/MarketingThemeScript.tsx
// so a stored/system preference renders without a flash.
//
// The header toggle is a QUICK SWITCH, not a durable setting: it flips
// light ⇄ dark. The choice lives in a cookie whose Max-Age is 24 hours, so
// the browser expires it on its own and the page falls back to the OS
// scheme (`system`) — no TTL bookkeeping needed here.

const THEME_CLASS = 'marketing-dark';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const prefersDark = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches;

// The quick-switch cookie if present (the browser drops it after 24h),
// otherwise `system`.
export const getMarketingTheme = (): ThemeMode =>
  getCookieStore().getItem(cookieKeys.marketingTheme) ?? 'system';

const resolveTheme = (mode: ThemeMode): 'light' | 'dark' =>
  mode === 'system' ? (prefersDark() ? 'dark' : 'light') : mode;

export const applyMarketingTheme = (mode: ThemeMode): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle(
    THEME_CLASS,
    resolveTheme(mode) === 'dark',
  );
};

// Module-level listeners so every hook instance (header toggle, SiteChrome)
// re-reads the same snapshot when any of them changes the theme.
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

const subscribe = (onStoreChange: () => void): (() => void) => {
  listeners.add(onStoreChange);
  // Track the OS scheme so `system` stays live; keep the class in sync too.
  const mql = window.matchMedia(DARK_QUERY);
  const onSchemeChange = () => {
    applyMarketingTheme(getMarketingTheme());
    onStoreChange();
  };
  mql.addEventListener('change', onSchemeChange);
  return () => {
    listeners.delete(onStoreChange);
    mql.removeEventListener('change', onSchemeChange);
  };
};

const getResolvedSnapshot = () => resolveTheme(getMarketingTheme());

// SSR snapshot: `dark` (the pre-theme look). The client snapshot takes over
// right after hydration. Keep first-paint visuals CSS-driven (via the
// `marketing-dark:` variant) rather than derived from this value, so nothing
// visibly swaps when the client snapshot arrives.
const getServerResolved = (): 'light' | 'dark' => 'dark';

export const useMarketingTheme = () => {
  const resolvedTheme = useSyncExternalStore(
    subscribe,
    getResolvedSnapshot,
    getServerResolved,
  );

  const setTheme = useCallback((mode: 'light' | 'dark') => {
    getCookieStore().setItem(cookieKeys.marketingTheme, mode, {
      maxAgeSeconds: MARKETING_THEME_COOKIE_MAX_AGE_SECONDS,
    });
    applyMarketingTheme(mode);
    notify();
  }, []);

  // Quick switch: flip whatever is currently showing.
  const toggleTheme = useCallback(
    () => setTheme(getResolvedSnapshot() === 'dark' ? 'light' : 'dark'),
    [setTheme],
  );

  return { resolvedTheme, setTheme, toggleTheme };
};
